import { useState, useRef, useCallback, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Camera, Upload, Volume2, Share2, Sparkles, RefreshCw, Flame, BookMarked, ArrowLeft, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { playAppAudio } from "@/lib/appAudio";
import { ttsOK } from "@/lib/languages";
import { useAuth } from "@/context/AuthContext";
import ConsentGate from "@/components/ConsentGate";
import Paywall from "@/components/Paywall";
import Footer from "@/components/Footer";
import { generateShareCard } from "@/lib/shareCard";
import { useI18n } from "@/context/I18nContext";

const PASTELS = ["bg-mint", "bg-lavender", "bg-butter", "bg-sky", "bg-peach"];

export default function LoopApp() {
  const { user, refresh } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showConsent, setShowConsent] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [signupPrompt, setSignupPrompt] = useState(false);
  const [pastel, setPastel] = useState("bg-mint");
  const [stream, setStream] = useState(null);
  const [camOn, setCamOn] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [speakingWord, setSpeakingWord] = useState(false);
  const videoRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => () => { if (stream) stream.getTracks().forEach(t => t.stop()); }, [stream]);

  // Capture referral param so attribution survives signup.
  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref) localStorage.setItem("fluent_ref", ref);
  }, []);

  const process = async (base64) => {
    setLoading(true);
    setResult(null);
    try {
      const res = await api.post("/loop/capture", {
        image: base64,
        target_lang: user?.target_lang, native_lang: user?.native_lang, level: user?.level,
      });
      setPastel(PASTELS[Math.floor(Math.random() * PASTELS.length)]);
      setResult(res.data);
      if (user) refresh();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 401 && d?.anon_cap_reached) {
        setSignupPrompt(true);
      } else if (e?.response?.status === 402) {
        setShowPaywall(true);
      } else {
        toast.error(typeof d === "string" ? d : t("capture.couldNotRead"));
      }
    } finally {
      setLoading(false);
      stopCam();
    }
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => process(reader.result.split(",")[1]);
    reader.readAsDataURL(file);
  };

  const startCam = async () => {
    if (!localStorage.getItem("fluent_consent_camera")) { setShowConsent(true); return; }
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      setStream(s); setCamOn(true);
      setTimeout(() => { if (videoRef.current) { videoRef.current.srcObject = s; videoRef.current.play(); } }, 100);
    } catch {
      toast.error("No camera available — upload a photo instead.");
      fileRef.current?.click();
    }
  };

  const stopCam = () => {
    if (stream) stream.getTracks().forEach(t => t.stop());
    setStream(null); setCamOn(false);
  };

  const snap = () => {
    const v = videoRef.current;
    if (!v) return;
    const canvas = document.createElement("canvas");
    canvas.width = v.videoWidth; canvas.height = v.videoHeight;
    canvas.getContext("2d").drawImage(v, 0, 0);
    process(canvas.toDataURL("image/jpeg", 0.8).split(",")[1]);
  };

  // Saved Vocab card audio only; signed-out trial gets no audio (no browser speech, no arbitrary TTS).
  const speak = async (vocabId) => {
    setSpeakingWord(true);
    try { const a = await playAppAudio(`/vocab/${vocabId}/audio`); a.onended = () => setSpeakingWord(false); a.onerror = () => setSpeakingWord(false); }
    catch { setSpeakingWord(false); toast.error("Voice unavailable right now."); }
  };

  const refParam = user?.user_id || localStorage.getItem("fluent_anon") || "guest";
  const shareText = result ? `I learned "${result.word}" (${result.translation}) today with FluentLoop 🌍` : "";
  const shareUrl = `${window.location.origin}/app?ref=${encodeURIComponent(refParam)}`;

  const share = useCallback(async () => {
    if (!result || sharing) return;
    setSharing(true);
    try {
      const blob = await generateShareCard(result, pastel);
      const file = new File([blob], "fluent-card.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: "FluentLoop", text: shareText, url: shareUrl });
          return;
        } catch (e) { if (e?.name === "AbortError") return; }
      }
      // Fallback: download the card image + copy the referral link.
      const dl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dl; a.download = `fluent-${(result.word || "word").replace(/\s+/g, "-")}.png`; a.click();
      URL.revokeObjectURL(dl);
      try { await navigator.clipboard.writeText(`${shareText} → ${shareUrl}`); } catch (e) { /* clipboard blocked */ }
      toast.success("Card saved! Link copied — go share it 🎉");
    } catch (e) {
      console.warn("Share failed", e);
      toast.error("Could not build the share card.");
    } finally { setSharing(false); }
  }, [result, pastel, sharing, shareText, shareUrl]);

  return (
    <div className="min-h-screen flex flex-col bg-paper">
      <header className="sticky top-0 z-40 bg-white border-b-2 border-ink">
        <div className="max-w-3xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-cabinet font-bold"><ArrowLeft className="w-5 h-5" /> Fluent<span className="text-coral">Loop</span></Link>
          {user ? (
            <div className="flex items-center gap-2">
              <span className="nb-tag bg-butter flex items-center gap-1"><Flame className="w-4 h-4" />{user.streak_count}</span>
              <Link to="/dashboard" className="nb-btn-outline px-4 py-2 text-sm">{t("missionRun.dashboard")}</Link>
            </div>
          ) : (
            <Link to="/signup" className="nb-btn-primary px-4 py-2 text-sm">{t("capture.signUpFree")}</Link>
          )}
        </div>
      </header>

      <main className="flex-1 max-w-3xl w-full mx-auto px-4 py-8">
        <div className="text-center mb-6">
          <div className="nb-tag bg-mint inline-block mb-3">LanguageLoop · Free</div>
          <h1 className="text-4xl sm:text-5xl font-cabinet font-extrabold">{t("capture.title")}</h1>
          <p className="text-gray-600 mt-2">{t("lensDesc.captureSub")}</p>
        </div>

        {/* Capture viewport */}
        <div className="nb-card p-4 md:p-6">
          <div className="relative rounded-2xl border-4 border-dashed border-ink overflow-hidden bg-paper aspect-[4/3] flex items-center justify-center">
            {camOn ? (
              <video ref={videoRef} className="absolute inset-0 w-full h-full object-cover" playsInline muted />
            ) : loading ? (
              <div className="text-center">
                <div className="w-14 h-14 border-4 border-ink border-t-coral rounded-full animate-spin mx-auto" />
                <p className="font-cabinet font-bold mt-4">{t("capture.reading")}</p>
              </div>
            ) : (
              <div className="text-center px-6">
                <Sparkles className="w-12 h-12 mx-auto stroke-[2]" />
                <p className="font-cabinet font-bold text-lg mt-3">{t("capture.pointHint")}</p>
                <p className="text-sm text-gray-500">{t("capture.anything")}</p>
              </div>
            )}
          </div>

          <div className="flex items-center justify-center gap-4 mt-5">
            {camOn ? (
              <>
                <button onClick={stopCam} className="nb-btn-outline w-14 h-14 rounded-full p-0"><X className="w-6 h-6" /></button>
                <button onClick={snap} data-testid="loop-snap-btn" className="w-20 h-20 bg-coral border-4 border-ink rounded-full shadow-hard hover:shadow-hard-sm hover:translate-x-[2px] hover:translate-y-[2px] transition-all flex items-center justify-center">
                  <Camera className="w-8 h-8 text-white stroke-[2.5]" />
                </button>
                <div className="w-14" />
              </>
            ) : (
              <>
                <button onClick={startCam} disabled={loading} data-testid="loop-camera-btn" className="nb-btn-primary px-6 py-4">
                  <Camera className="w-5 h-5" /> {t("lens.takePhoto")}
                </button>
                <button onClick={() => fileRef.current?.click()} disabled={loading} data-testid="loop-upload-btn" className="nb-btn-secondary px-6 py-4">
                  <Upload className="w-5 h-5" /> {t("lens.uploadImage")}
                </button>
              </>
            )}
            <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onFile} data-testid="loop-file-input" />
          </div>
          {result?.captures_remaining !== undefined && (
            <p className="text-center text-xs text-gray-500 mt-3">{result.captures_remaining} free captures left — sign up for more.</p>
          )}
        </div>

        {/* Result card */}
        <AnimatePresence>
          {result && (
            <motion.div initial={{ opacity: 0, scale: 0.85, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
              className={`nb-card ${pastel} mt-6 p-6`} data-testid="loop-result-card">
              <div className="flex items-start justify-between">
                <div className="nb-tag bg-white">{result.saved_to_vocab ? t("capture.savedToBank") : t("capture.learnMe")}</div>
                {!user && <span className="text-xs text-gray-500" data-testid="loop-signin-to-hear">Sign in to hear pronunciation</span>}
                {user && result.vocab_id && ttsOK(user.target_lang) && <button onClick={() => speak(result.vocab_id)} disabled={speakingWord} className="w-11 h-11 bg-white border-2 border-ink rounded-full flex items-center justify-center shadow-hard-sm" data-testid="loop-speak-btn">
                  {speakingWord ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Volume2 className="w-5 h-5" />}
                </button>}
              </div>
              <div className="font-cabinet font-extrabold text-5xl mt-4" data-testid="loop-word">{result.word}</div>
              <div className="text-lg font-bold text-gray-700">{result.translation}</div>
              <div className="text-sm font-mono mt-1">/{result.pronunciation}/</div>
              <div className="bg-white/70 border-2 border-ink rounded-xl p-4 mt-4">
                <p className="font-cabinet font-bold text-lg">{result.sentence}</p>
                <p className="text-sm text-gray-600 mt-1">{result.sentence_translation}</p>
              </div>
              <div className="flex gap-3 mt-5">
                <button onClick={share} disabled={sharing} data-testid="loop-share-btn" className="nb-btn-outline flex-1 py-3">
                  {sharing ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Share2 className="w-5 h-5" />} {sharing ? t("capture.building") : t("capture.shareCard")}
                </button>
                <button onClick={() => { setResult(null); }} className="nb-btn-primary flex-1 py-3"><RefreshCw className="w-5 h-5" /> {t("common.scanAgain")}</button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {user && (
          <Link to="/vocab" className="flex items-center justify-center gap-2 mt-6 text-sm font-bold text-gray-600 hover:text-coral">
            <BookMarked className="w-4 h-4" /> View your vocab bank
          </Link>
        )}
      </main>

      <ConsentGate type="camera" open={showConsent} onGrant={() => { setShowConsent(false); startCam(); }} onClose={() => setShowConsent(false)} />
      <Paywall open={showPaywall} onClose={() => setShowPaywall(false)} reason={t("capture.capReached")} />

      {/* Anon signup prompt */}
      <AnimatePresence>
        {signupPrompt && (
          <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-ink/60 backdrop-blur-sm">
            <motion.div initial={{ scale: 0.85, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="nb-card max-w-sm w-full p-6 text-center">
              <div className="text-5xl">🔥</div>
              <h3 className="font-cabinet font-extrabold text-2xl mt-3">{t("capture.keepStreak")}</h3>
              <p className="text-sm text-gray-600 mt-2">You've used your 3 free captures. Sign up free to keep learning, save your words, and build a streak.</p>
              <button onClick={() => navigate("/signup")} className="nb-btn-primary w-full mt-5 py-3" data-testid="anon-signup-btn">{t("capture.signUpFree")}</button>
              <button onClick={() => setSignupPrompt(false)} className="w-full text-sm text-gray-500 py-2 mt-1">{t("capture.close")}</button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
      <Footer />
    </div>
  );
}
